import { Controller } from "@hotwired/stimulus"

// Narrows period selects to the set of periods (evaluation calendar) the
// chosen class follows, and shows each period under its own calendar.
//
// Source options and period options carry `data-period-set-id` (empty for a
// common period; "*" for a class that follows no set, which is offered every
// period — PedagogicMonth.period_set_filter_key / .for_classroom). Period
// options also carry `data-period-set-name`.
//
// Before a class is chosen, or for a class without set, the periods of
// several calendars are shown under one group per calendar (« Mensuel »,
// « Unités d'apprentissage »), common periods first, ungrouped. Once a class
// is chosen, only its calendar and the common periods remain; one calendar
// alone needs no group.
//
// The options are removed rather than hidden: SlimSelect (initSlimSelects)
// builds its list from the DOM and shows hidden options greyed out, whether
// it starts before or after this controller.
export default class extends Controller {
  static targets = ["source", "options"]

  connect() {
    this.pools = new Map()
    this.filter()
  }

  filter() {
    if (!this.hasSourceTarget) return

    const chosen = this.sourceTarget.selectedOptions[0]
    const classChosen = Boolean(chosen && chosen.value)
    const classSet = (chosen && chosen.dataset.periodSetId) || ""
    const everySet = classSet === "*" || classSet === ""

    this.optionsTargets.forEach((select) => {
      const current = select.value
      const offered = this.poolFor(select).filter((option) => {
        const own = option.dataset.periodSetId || ""
        return !classChosen || everySet || own === "" || own === classSet
      })

      select.replaceChildren(...this.arrange(offered))
      select.value = offered.some((option) => option.value === current) ? current : ""
      this.refreshSlimSelect(select)
    })
  }

  // Every option the select was rendered with (optgroups flattened), kept to
  // put them back when another class is chosen.
  poolFor(select) {
    if (!this.pools.has(select)) this.pools.set(select, Array.from(select.options))
    return this.pools.get(select)
  }

  // Common options (prompt included) first, then one optgroup per calendar
  // when several calendars are offered.
  arrange(options) {
    const common = options.filter((option) => !option.dataset.periodSetId)
    const groups = new Map()
    options.filter((option) => option.dataset.periodSetId).forEach((option) => {
      const name = option.dataset.periodSetName || option.dataset.periodSetId
      if (!groups.has(name)) groups.set(name, [])
      groups.get(name).push(option)
    })
    if (groups.size < 2) return options

    const optgroups = Array.from(groups, ([name, members]) => {
      const group = document.createElement("optgroup")
      group.label = name
      group.append(...members)
      return group
    })
    return [...common, ...optgroups]
  }

  refreshSlimSelect(select) {
    if (!select.slim || typeof select.slim.setData !== "function") return

    const toData = (option) => ({
      text: option.text,
      value: option.value,
      placeholder: option.value === "",
      selected: option.selected,
    })
    select.slim.setData(Array.from(select.children).map((child) => (
      child.tagName === "OPTGROUP"
        ? { label: child.label, options: Array.from(child.children).map(toData) }
        : toData(child)
    )))
  }
}
